import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigService } from '../../../services/config.service';
import { UserService } from 'src/app/services/user.service';
import { BookClubSection } from '../../../models/user-book-clubs-collection';
import { Util } from '../../../util';
import { ApiService } from '../../../services/api.service';
import { UserActionsBookClubsComponent } from '../user-actions-book-clubs/user-actions-book-clubs.component';
import { AuthService } from '../../../services/auth.service';
import { BookClubCarousel } from 'src/app/models/bookclubcarousel';
import { LocaleService } from 'src/app/services/locale.service';
import { Actions, UserActions } from '../../../models/user-actions';
import { TranslateService } from '@ngx-translate/core';
import { catchError, timeout } from 'rxjs/operators';
import { of } from 'rxjs';
import { SearchModel } from 'src/app/models/search-model';
import { BookClub } from 'src/app/models/bookclub';

@Component({
  selector: 'app-user-book-clubs',
  templateUrl: './user-book-clubs.component.html',
  styleUrls: ['./user-book-clubs.component.scss'],
})
export class UserBookClubsComponent extends UserActionsBookClubsComponent implements OnInit {
  section = 'experiences';
  bookClubName: string;
  loading = true;
  bookClubUrl: string;
  bookSection = 'section=';
  bookClubSectionCreate: string;
  bookClubSectionHome: string;
  bookClubSectionGrades: string;
  bookClubSectionCertificates: string;
  bookClubsCollection: BookClubCarousel[];
  canCreateExperiences: boolean;
  oleTimeout: boolean = false;
  oleTimeoutName: string;

  learningExperiencesButtonLabel: string;
  allBookClubsEmpty: boolean;
  showGoToOleButton: boolean;

  constructor(
    public configService: ConfigService,
    private router: Router,
    public userService: UserService,
    public apiService: ApiService,
    public authService: AuthService,
    private translate: TranslateService,
    private localeService: LocaleService
  ) {
    super(configService, authService, apiService);
    this.bookClubUrl = configService.get().bookClubUrl;
    if (!this.bookClubUrl) {
      this.router.navigate(['/user/checkouts']);
    }
    this.bookClubName = configService.getUserItemValue(this.section);
    this.bookClubSectionCreate = BookClubSection.CREATE_BOOKCLUB;
    this.bookClubSectionHome = BookClubSection.HOME;
    this.bookClubSectionGrades = BookClubSection.MY_GRADES;
    this.bookClubSectionCertificates = BookClubSection.MY_CERTIFICATES;
    this.oleTimeoutName =
      this.configService.get().bookClubName || this.configService.translateByCustomerType('tooltip.bookClubName');
  }

  ngOnInit() {
    this.showGoToOleButton = this.configService.get().showGoToOleButton;
    const cachedBookClubs = sessionStorage.getItem(Util.BOOKCLUBS_KEY);
    if (this.userService.userActions.length === 0) {
      this.apiService.getOleConfiguration(this.userService.getUserId()).subscribe((oleConfig) => {
        this.authService.getLoggedUser().canCreateUserGroups = oleConfig.canCreateUserGroups;
        this.userService.userActions = [];
        if (oleConfig.canCreateExperiences && this.configService.get().showActionCreateExperience) {
          this.canCreateExperiences = true;
          const actionCreateExperience = {} as UserActions;
          actionCreateExperience.buttonName = this.translate.instant('user.actions.createLearningExperience');
          actionCreateExperience.action = Actions.CREATE_BOOKCLUB;
          this.userService.userActions.push(actionCreateExperience);
        }
      });
    } else {
      this.canCreateExperiences = !(
        this.userService.userActions.findIndex((userAction) => userAction.action === Actions.CREATE_BOOKCLUB) < 0
      );
    }
    if (cachedBookClubs) {
      this.bookClubsCollection = JSON.parse(cachedBookClubs);
      this.allBookClubsEmpty = this.checkArraysEmpty(this.bookClubsCollection);
    }
    if (cachedBookClubs === null || this.bookClubsCollection.length === 0) {
      this.oleTimeout = false;
      this.apiService.getBookClubs(this.userService.getUserId()).subscribe({
        next: (bookClubsCollection) => {
          sessionStorage.setItem(Util.BOOKCLUBS_KEY, JSON.stringify(bookClubsCollection));
          this.bookClubsCollection = bookClubsCollection;

          this.allBookClubsEmpty = this.checkArraysEmpty(this.bookClubsCollection);

          if (this.bookClubsCollection.length > 0) {
            this.loading = false;
          }
        },
        error: (e) => {
          if (e.name === 'TimeoutError') {
            this.oleTimeout = true;
            this.loading = false;
          }
        },
      });
    } else {
      this.loading = false;
    }

    this.learningExperiencesButtonLabel = this.getLearningExperiencesButtonLabel();
  }

  getBookClubName(carousel: BookClubCarousel): string {
    if (Object.keys(carousel.topTenTitles).includes(this.localeService.language)) {
      return carousel.topTenTitles[this.localeService.language];
    }

    if (Object.keys(carousel.topTenTitles).includes(Util.DEFAULT_LANGUAGE_CODE_FOR_CAROUSELS)) {
      return carousel.topTenTitles[Util.DEFAULT_LANGUAGE_CODE_FOR_CAROUSELS];
    }

    return carousel.topTenTitles['en'];
  }

  exploreLearningExperiences() {
    const model = new SearchModel();
    model.explore = true;

    this.router.navigate(['/results'], {
      queryParams: model.toRequestLearningExperiences(),
    });
  }

  private checkArraysEmpty(bookClubsCollection: BookClubCarousel[]) {
    if (bookClubsCollection.length === 0) {
      return true;
    }
    return bookClubsCollection.every((collection) => collection.bookClubs.length === 0);
  }

  private getLearningExperiencesButtonLabel() {
    const fullPrecisionButtonLabel = this.configService.get().exploreExperiencesButtonLabel;
    const learningExperiencesBrandingName = this.configService.get().bookClubName;

    if (Util.isEmptyString(fullPrecisionButtonLabel)) {
      return Util.isEmptyString(learningExperiencesBrandingName)
        ? this.localeService.instant('search.learningExperiences')
        : this.localeService.instant('search.exploreLearningExperiences', {
            brandingName: learningExperiencesBrandingName.toLocaleLowerCase(),
          });
    }

    return fullPrecisionButtonLabel;
  }
}
